/**
 * @flm9/telemetry-client — browser side of the debug flight recorder
 * (full session debugging, phase 2). Used by the referee PWA, the board and
 * the web app; framework-agnostic.
 *
 * While the server says "record" (the training is live — GET /api/telemetry)
 * and the app allows it (e.g. the board only after the PIN gate), it
 * captures and sends to POST /api/telemetry:
 *   - every same-origin /api/ call (tRPC): procedure, status, duration,
 *     error text — and adds `traceparent` + `x-flm9-surface/device` headers
 *     so the server spans join the browser trace;
 *   - clicks / navigation keys as "interactions": API calls started within
 *     3 s join the interaction's trace (click "Weiter" → tRPC → DB → SSE);
 *   - SSE events received (with the server delivery span as parent and the
 *     delivery delay), open / error;
 *   - errors, unhandled rejections, console.warn/error, navigation,
 *     visibility and online/offline;
 *   - explicit `track(name, intent).ok(outcome) / .fail(err)` — "what was
 *     supposed to happen" vs "what happened";
 *   - an rrweb DOM recording (lazy-loaded), chunked every 10 s.
 * Batches that can't be sent wait in IndexedDB (offline PWA) and go out
 * later; on page close the tail goes out via navigator.sendBeacon.
 *
 * Privacy: request/response BODIES are never captured client-side (the
 * server's redacted tRPC snapshot covers them); text inputs are not
 * recorded as interactions; rrweb masks passwords, textareas and
 * credential-looking fields (`maskInput`) and anything under
 * [data-telemetry-private]; [data-telemetry-block] is not recorded at all;
 * `domRecordingFor` limits where DOM recording runs (web: training pages).
 * The server redacts everything again before storing.
 */
import { newSpanId, newTraceId, parseTraceparent, traceparent, uuid } from './ids.js';
import { dropBatch, peekBatch, storeBatch } from './outbox.js';

export type Surface = 'referee' | 'board' | 'web';

export interface ClientEvent {
  ts: number;
  kind: string;
  name: string;
  traceId?: string;
  spanId?: string;
  parentSpanId?: string;
  status?: 'info' | 'ok' | 'fail';
  durationMs?: number;
  intent?: unknown;
  outcome?: unknown;
  data?: unknown;
}

/** rrweb's `record()` signature, narrowed to what we use. */
export type RecordFn = ((
  opts: Record<string, unknown> & { emit: (e: unknown) => void },
) => (() => void) | undefined) & {
  /** rrweb: emit a full DOM snapshot right now. */
  takeFullSnapshot?: (isCheckout?: boolean) => void;
};

/** Seconds after a bug press when the second full snapshot is taken. */
export const BUG_AFTER_MS = 4_000;

export interface TelemetryClientOptions {
  surface: Surface;
  /** Current training id (null = none selected). Polled every few seconds. */
  getSessionId: () => string | null;
  /** Board kiosk token for the current training (board only). */
  getBoardToken?: () => string | undefined;
  endpoint?: string;
  /** False until the app passed its auth screen (board PIN gate). Default true. */
  recordingAllowed?: boolean;
  /** rrweb DOM recording on top of events. Default true. */
  domRecording?: boolean;
  /**
   * Where DOM recording may run (checked on every navigation / tick). The
   * web app limits it to training pages so settings / user pages with
   * credentials are never recorded. Default: everywhere.
   */
  domRecordingFor?: (location: Location) => boolean;
  flushIntervalMs?: number;
  statusIntervalMs?: number;
  recordingFlushMs?: number;
  /** Override the rrweb loader (tests). */
  loadRecorder?: () => Promise<RecordFn>;
}

export interface TrackHandle {
  readonly traceId: string;
  readonly spanId: string;
  ok(outcome?: unknown): void;
  fail(error: unknown, outcome?: unknown): void;
}

export interface TelemetryClient {
  readonly deviceId: string;
  /**
   * `background: true` (sync uploads): don't become the current interaction —
   * pass `traceparent(handle)` on the request instead.
   */
  track(name: string, intent?: unknown, opts?: { background?: boolean }): TrackHandle;
  event(kind: string, name: string, data?: unknown, status?: ClientEvent['status']): void;
  setRecordingAllowed(allowed: boolean): void;
  /**
   * "Fehler merken": a `bug` marker in the timeline plus a full DOM snapshot
   * now and ${BUG_AFTER_MS / 1000} s later, uploaded right away (the seconds
   * before are in the running recording). Returns the marker's trace id.
   */
  markBug(bugId: string, context?: Record<string, unknown>): { traceId: string };
  flush(): Promise<void>;
  /** 'unknown' until the first status answer for the current training. */
  readonly state: 'unknown' | 'on' | 'off';
}

const MAX_QUEUE = 5_000;
const MAX_BATCH_EVENTS = 500;
const BEACON_LIMIT = 60_000;
const INTERACTION_MS = 3_000;
const DEVICE_KEY = 'flm9.telemetry.device';

const GLOBAL = Symbol.for('flm9.telemetry-client');
type G = typeof globalThis & { [GLOBAL]?: Client };

function str(value: unknown, max = 500): string {
  let s: string;
  if (typeof value === 'string') s = value;
  else if (value instanceof Error) s = `${value.name}: ${value.message}`;
  else {
    try {
      s = JSON.stringify(value) ?? String(value);
    } catch {
      s = String(value);
    }
  }
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

function errorText(err: unknown): string {
  return err instanceof Error ? `${err.name}: ${err.message}` : str(err, 300);
}

function deviceIdFromStorage(): string {
  try {
    const existing = localStorage.getItem(DEVICE_KEY);
    if (existing) return existing;
    const id = uuid();
    localStorage.setItem(DEVICE_KEY, id);
    return id;
  } catch {
    return uuid();
  }
}

const MAX_NAME = 300;
const MAX_KIND = 40;

/** Event name for an API call; long tRPC batches are summarised (full list in `data.procedures`). */
function procedureOf(url: URL): { name: string; procedures: string[] | null } {
  const m = /^\/api\/trpc\/(.+)$/.exec(url.pathname);
  if (!m) return { name: url.pathname.slice(0, MAX_NAME), procedures: null };
  const list = decodeURIComponent(m[1]!).split(',');
  const full = `trpc ${list.join(',')}`;
  if (full.length <= MAX_NAME) return { name: full, procedures: list.length > 1 ? list : null };
  return { name: `trpc batch(${list.length}): ${list[0]}, …`.slice(0, MAX_NAME), procedures: list };
}

/** First tRPC error of a (batch) error body: "CODE: message [appCode]". */
async function errorOfResponse(res: Response): Promise<string> {
  return (await batchErrorOf(res)) ?? `HTTP ${res.status}`;
}

/** The first tRPC error envelope in a (batch) body, or null when there is none. */
async function batchErrorOf(res: Response): Promise<string | null> {
  try {
    const body = (await res.json()) as unknown;
    const first = Array.isArray(body)
      ? body.find((b) => b && typeof b === 'object' && 'error' in b)
      : body;
    const err = (
      first as { error?: { message?: string; data?: { code?: string; appCode?: string | null } } }
    )?.error;
    if (err) {
      return `${err.data?.code ?? res.status}: ${err.message ?? ''}${err.data?.appCode ? ` [${err.data.appCode}]` : ''}`.slice(
        0,
        300,
      );
    }
  } catch {
    // not JSON
  }
  return null;
}

function labelOf(el: Element): string {
  const aria = el.getAttribute('aria-label') ?? el.getAttribute('title');
  if (aria) return aria.trim().slice(0, 80);
  if (el instanceof HTMLInputElement) return `${el.type}${el.name ? ` ${el.name}` : ''}`;
  const text = (el as HTMLElement).innerText ?? el.textContent ?? '';
  const clean = text.replace(/\s+/g, ' ').trim();
  return (clean || el.tagName.toLowerCase()).slice(0, 80);
}

const INTERACTIVE =
  'button,a,[role=button],[role=tab],[role=menuitem],[role=switch],[role=option],summary,label,select,input[type=checkbox],input[type=radio]';

function isTextTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  if (t instanceof HTMLTextAreaElement) return true;
  if (t instanceof HTMLInputElement) {
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range'].includes(t.type);
  }
  return false;
}

/** Every input kind rrweb knows → masked, then `maskInput` decides per element. */
const ALL_INPUTS = Object.fromEntries(
  [
    'color',
    'date',
    'datetime-local',
    'email',
    'month',
    'number',
    'range',
    'search',
    'tel',
    'text',
    'time',
    'url',
    'week',
    'textarea',
    'select',
    'password',
  ].map((k) => [k, true]),
);
const SENSITIVE_FIELD = /pass|pin|secret|token|key|otp|code|iban|card|cvc|credential/i;

/**
 * rrweb input masking: keep ordinary values readable (scores, names,
 * search) but never record passwords, textareas (keys / JSON secrets are
 * pasted there), anything whose name / id / autocomplete / label looks like
 * a credential, or anything under [data-telemetry-private].
 */
export function maskInput(text: string, element: HTMLElement | null): string {
  const masked = '*'.repeat(Math.min(text.length, 32));
  if (!element) return masked;
  if (element instanceof HTMLTextAreaElement) return masked;
  if (element.closest?.('[data-telemetry-private],[data-telemetry-block]')) return masked;
  if (element instanceof HTMLInputElement && element.type === 'password') return masked;
  const hints = ['name', 'id', 'autocomplete', 'aria-label', 'placeholder', 'inputmode']
    .map((a) => element.getAttribute(a) ?? '')
    .join(' ');
  return SENSITIVE_FIELD.test(hints) ? masked : text;
}

class Client implements TelemetryClient {
  readonly deviceId = deviceIdFromStorage();
  state: 'unknown' | 'on' | 'off' = 'unknown';
  private queue: ClientEvent[] = [];
  private dropped = 0;
  private allowed: boolean;
  private serverRecord = false;
  /** `undefined` until the first tick, so it always asks once — also without a training (web /admin). */
  private lastSessionId: string | null | undefined = undefined;
  private interaction: { traceId: string; spanId: string; until: number } | null = null;
  private readonly endpoint: string;
  private readonly origFetch: typeof fetch;
  private recBuf: unknown[] = [];
  private recSeq = 0;
  private recordingId: string | null = null;
  private recChunkStart = 0;
  private lastRecFlush = 0;
  private stopRecorder: (() => void) | null = null;
  private recordFn: RecordFn | null = null;
  private starting = false;
  /** Bumped by stopRecording: a start still waiting for the lazy rrweb import is void. */
  private recorderGeneration = 0;
  private flushing = false;
  private timers: ReturnType<typeof setInterval>[] = [];
  /** Server said 429: send nothing new until then (batches go to the outbox). */
  private pausedUntil = 0;

  constructor(private readonly opts: TelemetryClientOptions) {
    this.endpoint = opts.endpoint ?? '/api/telemetry';
    this.allowed = opts.recordingAllowed ?? true;
    // Not allowed yet (board before its PIN gate): no requests, no queue.
    if (!this.allowed) this.state = 'off';
    this.origFetch = window.fetch.bind(window);
    this.patchFetch();
    this.patchEventSource();
    this.listen();
    this.push({
      kind: 'nav',
      name: `load ${location.pathname}`,
      data: {
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        online: navigator.onLine,
      },
    });
    this.timers.push(setInterval(() => void this.tick(), 5_000));
    this.timers.push(setInterval(() => void this.checkStatus(), opts.statusIntervalMs ?? 60_000));
    this.timers.push(setInterval(() => void this.flush(), opts.flushIntervalMs ?? 5_000));
    void this.tick();
  }

  // ---- public API ---------------------------------------------------------

  track(name: string, intent?: unknown, opts: { background?: boolean } = {}): TrackHandle {
    const parent = opts.background ? null : this.liveInteraction();
    const traceId = parent?.traceId ?? newTraceId();
    const spanId = newSpanId();
    const started = Date.now();
    const t0 = performance.now();
    if (!opts.background) this.interaction = { traceId, spanId, until: Date.now() + 60_000 };
    let done = false;
    const finish = (status: 'ok' | 'fail', outcome: unknown) => {
      if (done) return;
      done = true;
      if (this.interaction?.spanId === spanId) this.interaction.until = Date.now() + INTERACTION_MS;
      this.push({
        ts: started,
        kind: 'intent',
        name,
        traceId,
        spanId,
        ...(parent ? { parentSpanId: parent.spanId } : {}),
        status,
        durationMs: performance.now() - t0,
        intent,
        outcome,
      });
    };
    return {
      traceId,
      spanId,
      ok: (outcome?: unknown) => finish('ok', outcome ?? null),
      fail: (error: unknown, outcome?: unknown) =>
        finish('fail', {
          error: errorText(error),
          ...(outcome !== undefined ? { detail: outcome } : {}),
        }),
    };
  }

  event(kind: string, name: string, data?: unknown, status: ClientEvent['status'] = 'info'): void {
    const parent = this.liveInteraction();
    this.push({
      kind,
      name,
      status,
      data,
      traceId: parent?.traceId ?? newTraceId(),
      spanId: newSpanId(),
      ...(parent ? { parentSpanId: parent.spanId } : {}),
    });
  }

  markBug(bugId: string, context: Record<string, unknown> = {}): { traceId: string } {
    // A training opened moments ago is otherwise only noticed on the next
    // 5 s tick (state 'off' until then would drop the marker): run the
    // switch now — its synchronous part sets 'unknown' before the status
    // request, so the marker queues and goes out once recording is on.
    if (this.opts.getSessionId() !== this.lastSessionId) void this.tick();
    const traceId = newTraceId();
    this.push({
      kind: 'bug',
      name: 'Fehler gemerkt',
      status: 'fail',
      traceId,
      data: { bugId, page: location.pathname, recordingId: this.recordingId, ...context },
    });
    // The +4 s capture belongs to the training (and recording) of the press:
    // after a switch it is skipped rather than filed under the new one.
    const pressedSession = this.opts.getSessionId();
    const pressedGeneration = this.recorderGeneration;
    const snapshotAndSend = (phase: 'now' | 'after') => {
      if (
        phase === 'after' &&
        (this.opts.getSessionId() !== pressedSession ||
          this.recorderGeneration !== pressedGeneration)
      )
        return;
      try {
        if (this.stopRecorder) this.recordFn?.takeFullSnapshot?.(false);
      } catch {
        // a failed snapshot must not break the button
      }
      if (phase === 'after')
        this.push({ kind: 'bug', name: 'Fehler gemerkt (+4 s)', traceId, data: { bugId } });
      if (this.state !== 'on') return;
      const sid = this.opts.getSessionId();
      const body = this.takeBatch(true, sid);
      if (body) void this.send(body, sid);
    };
    snapshotAndSend('now');
    setTimeout(() => snapshotAndSend('after'), BUG_AFTER_MS);
    return { traceId };
  }

  setRecordingAllowed(allowed: boolean): void {
    if (this.allowed === allowed) return;
    this.allowed = allowed;
    if (allowed) {
      // Only now ask the server — nothing is sent before the gate opens.
      this.state = 'unknown';
      void this.checkStatus();
    } else {
      this.applyState();
    }
  }

  async flush(): Promise<void> {
    if (this.flushing) return;
    this.flushing = true;
    try {
      if (this.state === 'on') {
        const body = this.takeBatch(false);
        if (body) await this.send(body, this.opts.getSessionId());
      }
      await this.drainOutbox();
    } finally {
      this.flushing = false;
    }
  }

  // ---- state --------------------------------------------------------------

  private async tick(): Promise<void> {
    const sid = this.opts.getSessionId();
    if (sid !== this.lastSessionId) {
      const previous = this.lastSessionId;
      this.lastSessionId = sid;
      // Finalize everything buffered for the previous training under ITS id
      // and end its recording; the next training gets a fresh recording.
      if (this.state === 'on') {
        let body = this.takeBatch(true, previous ?? null);
        while (body) {
          void this.send(body, previous ?? null);
          body = this.takeBatch(true, previous ?? null);
        }
      }
      this.stopRecording();
      if (!this.allowed) {
        this.state = 'off';
        return;
      }
      this.state = 'unknown';
      await this.checkStatus();
      return;
    }
    this.syncRecorder();
  }

  /** Start / stop rrweb when the page (web: training pages only) changes. */
  private syncRecorder(): void {
    if (this.state !== 'on') return;
    const allowedHere = this.opts.domRecordingFor ? this.opts.domRecordingFor(location) : true;
    if (allowedHere && !this.stopRecorder) void this.startRecording();
    else if (!allowedHere && this.stopRecorder) this.finalizeRecording();
  }

  /**
   * Stop rrweb and send what it recorded so far (incl. its final events)
   * under the current training — leaving a training page for an excluded
   * one (web: settings) must not lose the recording's tail.
   */
  private finalizeRecording(): void {
    this.stopRecorder?.();
    this.stopRecorder = null;
    // The buffer belongs to the training it was recorded under: the page we
    // navigate to may already name another one (or none — e.g. the web
    // app's debug page), which the next tick only picks up later.
    const sid = this.lastSessionId !== undefined ? this.lastSessionId : this.opts.getSessionId();
    const body = this.takeBatch(true, sid);
    if (body) void this.send(body, sid);
    this.stopRecording();
  }

  private async checkStatus(): Promise<void> {
    if (!this.allowed) return;
    const sid = this.opts.getSessionId();
    // Referee / board record only for an open training — no request without one.
    if (!sid && this.opts.surface !== 'web') {
      this.serverRecord = false;
      this.applyState();
      return;
    }
    const q = new URLSearchParams({ surface: this.opts.surface });
    if (sid) q.set('sessionId', sid);
    try {
      const res = await this.origFetch(`${this.endpoint}?${q.toString()}`, {
        cache: 'no-store',
        credentials: 'same-origin',
      });
      if (!res.ok) return;
      const body = (await res.json()) as { record?: boolean };
      // Session changed while we waited → the next tick re-asks.
      if (sid !== this.opts.getSessionId()) return;
      this.serverRecord = body.record === true;
      this.applyState();
    } catch {
      // offline: keep the current state (events keep queueing → outbox)
    }
  }

  private applyState(): void {
    // Called only after an answer (status, 409/403) or a gate change:
    // anything but "record + allowed" is OFF — nothing queues in 'off'.
    const on = this.serverRecord && this.allowed;
    this.state = on ? 'on' : 'off';
    if (on) {
      this.syncRecorder();
    } else {
      this.queue = [];
      this.stopRecording();
    }
  }

  private push(ev: Omit<ClientEvent, 'ts'> & { ts?: number }): void {
    // A training opened (or left) moments ago would otherwise only be
    // noticed on the next 5 s tick — until then the recorder may still be
    // 'off' (coming from the session list) and drop this event, or file it
    // under the previous training. Run the switch now: its synchronous part
    // finalizes the old buffer under the old id and sets 'unknown' before
    // the status request, so the event queues for the new training.
    if (this.opts.getSessionId() !== this.lastSessionId) void this.tick();
    if (this.state === 'off') return;
    // Every event gets a span id: it is also the server's de-dup key, so a
    // batch replayed after a lost response never stores an event twice.
    this.queue.push({
      ts: Date.now(),
      ...ev,
      // Never exceed the server's field limits — one oversized name would
      // get the whole batch (and its recording chunk) rejected.
      kind: ev.kind.slice(0, MAX_KIND),
      name: ev.name.slice(0, MAX_NAME),
      spanId: ev.spanId ?? newSpanId(),
    } as ClientEvent);
    if (this.queue.length > MAX_QUEUE) {
      this.queue.splice(0, this.queue.length - MAX_QUEUE);
      this.dropped += 1;
    }
  }

  private liveInteraction() {
    const i = this.interaction;
    return i && Date.now() < i.until ? i : null;
  }

  private startInteraction(kind: string, name: string, data?: unknown): void {
    const traceId = newTraceId();
    const spanId = newSpanId();
    this.interaction = { traceId, spanId, until: Date.now() + INTERACTION_MS };
    this.push({ kind, name, traceId, spanId, data });
  }

  // ---- sending ------------------------------------------------------------

  private envelope(events: ClientEvent[], recording: unknown, sessionId: string | null): string {
    return JSON.stringify({
      sessionId,
      surface: this.opts.surface,
      deviceId: this.deviceId,
      ...(this.opts.getBoardToken?.() ? { boardToken: this.opts.getBoardToken() } : {}),
      events,
      ...(recording ? { recording } : {}),
    });
  }

  /** Next batch body (events + due recording chunk), or null when empty. */
  private takeBatch(
    force: boolean,
    sessionId: string | null = this.opts.getSessionId(),
  ): string | null {
    const events = this.queue.splice(0, MAX_BATCH_EVENTS);
    if (this.dropped > 0) {
      events.push({
        ts: Date.now(),
        kind: 'client',
        name: 'queue overflow',
        status: 'fail',
        data: { dropped: this.dropped },
      });
      this.dropped = 0;
    }
    let recording: unknown;
    const due = force || Date.now() - this.lastRecFlush >= (this.opts.recordingFlushMs ?? 10_000);
    if (this.recordingId && this.recBuf.length > 0 && (due || this.recBuf.length > 20_000)) {
      const chunk = this.recBuf.splice(0);
      recording = {
        recordingId: this.recordingId,
        seq: this.recSeq++,
        startedAt: this.recChunkStart,
        endedAt: Date.now(),
        events: chunk,
      };
      this.recChunkStart = Date.now();
      this.lastRecFlush = Date.now();
    }
    if (events.length === 0 && !recording) return null;
    return this.envelope(events, recording, sessionId);
  }

  /** POST one batch; on network/server trouble it goes to the outbox. */
  /** `sessionId` = the training this batch belongs to (rejections only matter for the current one). */
  private async send(
    body: string,
    sessionId: string | null,
  ): Promise<'sent' | 'stored' | 'rejected'> {
    if (Date.now() < this.pausedUntil) {
      await storeBatch(body);
      return 'stored';
    }
    try {
      const res = await this.origFetch(this.endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        credentials: 'same-origin',
        keepalive: body.length < BEACON_LIMIT,
      });
      if (res.ok) return 'sent';
      if (res.status === 409 || res.status === 403) {
        // Training no longer live / not allowed: stop until the next status —
        // but only if the batch belongs to the CURRENT training (a late 409
        // for the training we just left must not switch off the new one).
        if (sessionId === this.opts.getSessionId()) {
          this.serverRecord = false;
          this.applyState();
        }
        return 'rejected';
      }
      if (res.status === 400 || res.status === 413) return 'rejected';
      if (res.status === 429) this.pausedUntil = Date.now() + 60_000;
      await storeBatch(body);
      return 'stored';
    } catch {
      await storeBatch(body);
      return 'stored';
    }
  }

  private async drainOutbox(): Promise<void> {
    if (Date.now() < this.pausedUntil) return;
    for (let i = 0; i < 5; i++) {
      const batch = await peekBatch();
      if (!batch) return;
      try {
        const res = await this.origFetch(this.endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: batch.body,
          credentials: 'same-origin',
        });
        // Sent, or permanently unacceptable (not live any more, invalid) → drop.
        if (res.ok || [400, 403, 409, 413].includes(res.status)) await dropBatch(batch);
        else {
          if (res.status === 429) this.pausedUntil = Date.now() + 60_000;
          return;
        }
      } catch {
        return;
      }
    }
  }

  private onPageHide = (): void => {
    if (this.state !== 'on') return;
    let body = this.takeBatch(true);
    while (body) {
      const sent =
        body.length < BEACON_LIMIT &&
        typeof navigator.sendBeacon === 'function' &&
        navigator.sendBeacon(this.endpoint, new Blob([body], { type: 'text/plain' }));
      if (!sent) void storeBatch(body);
      body = this.takeBatch(true);
    }
  };

  // ---- rrweb --------------------------------------------------------------

  private async startRecording(): Promise<void> {
    if (this.opts.domRecording === false || this.stopRecorder || this.starting) return;
    this.starting = true;
    const generation = this.recorderGeneration;
    try {
      const record = this.opts.loadRecorder
        ? await this.opts.loadRecorder()
        : ((await import('@rrweb/record')).record as unknown as RecordFn);
      // Re-check after the (lazy) import: stopped meanwhile, recording turned
      // off, or the page changed to one where DOM recording isn't allowed.
      const allowedHere = this.opts.domRecordingFor ? this.opts.domRecordingFor(location) : true;
      if (generation !== this.recorderGeneration || this.state !== 'on' || !allowedHere) return;
      this.recordingId = uuid();
      this.recSeq = 0;
      this.recBuf = [];
      this.recChunkStart = Date.now();
      this.lastRecFlush = Date.now();
      const stop = record({
        emit: (e: unknown) => {
          this.recBuf.push(e);
        },
        maskInputOptions: ALL_INPUTS,
        maskInputFn: maskInput,
        maskTextSelector: '[data-telemetry-private]',
        blockSelector: '[data-telemetry-block]',
        // A fresh full snapshot every 10 min keeps later chunks replayable
        // on their own (long trainings, lost chunks).
        checkoutEveryNms: 10 * 60_000,
        sampling: { mousemove: 100, scroll: 150, input: 'last' },
        recordCanvas: false,
        collectFonts: false,
      });
      this.stopRecorder = stop ?? null;
      this.recordFn = record;
      this.push({ kind: 'recording', name: 'start', data: { recordingId: this.recordingId } });
    } catch (err) {
      this.push({
        kind: 'recording',
        name: 'start failed',
        status: 'fail',
        data: { error: errorText(err) },
      });
    } finally {
      this.starting = false;
    }
  }

  private stopRecording(): void {
    this.recorderGeneration += 1;
    this.stopRecorder?.();
    this.stopRecorder = null;
    this.recordingId = null;
    this.recBuf = [];
  }

  // ---- instrumentation ----------------------------------------------------

  private patchFetch(): void {
    const orig = this.origFetch;
    const self = this;
    const endpointPath = new URL(this.endpoint, location.href).pathname;
    window.fetch = async function tracedFetch(input: RequestInfo | URL, init?: RequestInit) {
      let url: URL;
      try {
        url = new URL(
          typeof input === 'string' ? input : input instanceof URL ? input.href : input.url,
          location.href,
        );
      } catch {
        return orig(input, init);
      }
      if (
        url.origin !== location.origin ||
        !url.pathname.startsWith('/api/') ||
        url.pathname === endpointPath
      ) {
        return orig(input, init);
      }
      const headers = new Headers(
        init?.headers ?? (input instanceof Request ? input.headers : undefined),
      );
      // An explicit traceparent (background `track()`, e.g. sync uploads)
      // wins over the current click interaction.
      const explicit = parseTraceparent(headers.get('traceparent'));
      const parent = explicit
        ? { traceId: explicit.traceId, spanId: explicit.spanId }
        : self.liveInteraction();
      const traceId = parent?.traceId ?? newTraceId();
      const spanId = newSpanId();
      headers.set('traceparent', traceparent(traceId, spanId));
      headers.set('x-flm9-surface', self.opts.surface);
      headers.set('x-flm9-device', self.deviceId);
      const method = (
        init?.method ?? (input instanceof Request ? input.method : 'GET')
      ).toUpperCase();
      const { name, procedures } = procedureOf(url);
      const started = Date.now();
      const t0 = performance.now();
      const base = {
        ts: started,
        kind: 'fetch',
        name,
        traceId,
        spanId,
        ...(parent ? { parentSpanId: parent.spanId } : {}),
      };
      try {
        const res = await orig(input, { ...init, headers });
        const durationMs = performance.now() - t0;
        // HTTP 207 = mixed tRPC batch: some calls failed although res.ok.
        const batchError = res.status === 207 ? await batchErrorOf(res.clone()) : null;
        if (res.ok && !batchError) {
          self.push({
            ...base,
            status: 'ok',
            durationMs,
            data: { method, status: res.status, ...(procedures ? { procedures } : {}) },
          });
        } else if (batchError) {
          self.push({
            ...base,
            status: 'fail',
            durationMs,
            data: {
              method,
              status: res.status,
              partial: true,
              ...(procedures ? { procedures } : {}),
            },
            outcome: { error: batchError },
          });
        } else {
          const error = await errorOfResponse(res.clone());
          self.push({
            ...base,
            status: 'fail',
            durationMs,
            data: { method, status: res.status, ...(procedures ? { procedures } : {}) },
            outcome: { error },
          });
        }
        return res;
      } catch (err) {
        self.push({
          ...base,
          status: 'fail',
          durationMs: performance.now() - t0,
          data: { method, status: 0 },
          outcome: { error: `network: ${errorText(err)}` },
        });
        throw err;
      }
    } as typeof fetch;
  }

  private patchEventSource(): void {
    const Orig = window.EventSource;
    if (typeof Orig !== 'function') return;
    const self = this;
    const recordSse = (source: EventSource, type: string, ev: Event) => {
      if (type === 'ping') return;
      const raw = (ev as MessageEvent).data;
      let parsed: Record<string, unknown> | null = null;
      if (typeof raw === 'string') {
        try {
          const v = JSON.parse(raw) as unknown;
          if (v && typeof v === 'object' && !Array.isArray(v))
            parsed = v as Record<string, unknown>;
        } catch {
          parsed = null;
        }
      }
      const tp = parseTraceparent(parsed?.tp);
      const at = typeof parsed?.at === 'number' ? parsed.at : null;
      const atMs = at === null ? null : at < 1e12 ? at * 1000 : at;
      const { tp: _tp, ...payload } = parsed ?? {};
      void _tp;
      self.push({
        kind: 'sse',
        name: type,
        traceId: tp?.traceId ?? newTraceId(),
        spanId: newSpanId(),
        ...(tp ? { parentSpanId: tp.spanId } : {}),
        data: {
          url: new URL(source.url, location.href).pathname,
          delayMs: atMs === null ? null : Date.now() - atMs,
          bytes: typeof raw === 'string' ? raw.length : null,
          payload: !parsed
            ? null
            : JSON.stringify(payload).length <= 2_000
              ? payload
              : { truncated: true, keys: Object.keys(payload).slice(0, 30) },
        },
      });
    };
    type Add = (
      type: string,
      listener: EventListenerOrEventListenerObject | null,
      options?: boolean | AddEventListenerOptions,
    ) => void;
    // A constructor function returning a REAL EventSource (instanceof keeps
    // working) whose addEventListener also attaches our recorder once per
    // event type. User listeners are untouched (removeEventListener works).
    const Traced = function TracedEventSource(url: string | URL, config?: EventSourceInit) {
      const es = new Orig(url, config);
      const nativeAdd = es.addEventListener.bind(es) as Add;
      const types = new Set<string>();
      const watch = (type: string) => {
        if (types.has(type) || type === 'open' || type === 'error') return;
        types.add(type);
        nativeAdd(type, (ev: Event) => recordSse(es, type, ev));
      };
      (es as unknown as { addEventListener: Add }).addEventListener = (type, listener, options) => {
        watch(type);
        nativeAdd(type, listener, options);
      };
      const path = new URL(es.url, location.href).pathname;
      nativeAdd('open', () => self.push({ kind: 'sse', name: 'open', data: { url: path } }));
      nativeAdd('error', () =>
        self.push({
          kind: 'sse',
          name: 'error',
          status: 'fail',
          data: { url: path, readyState: es.readyState },
        }),
      );
      watch('message');
      return es;
    } as unknown as typeof EventSource;
    Object.assign(Traced, { CONNECTING: 0, OPEN: 1, CLOSED: 2 });
    (Traced as unknown as { prototype: EventSource }).prototype = Orig.prototype;
    window.EventSource = Traced;
  }

  private listen(): void {
    document.addEventListener(
      'click',
      (ev) => {
        const target = ev.target instanceof Element ? ev.target.closest(INTERACTIVE) : null;
        if (!target || target.closest('[data-telemetry-private],[data-telemetry-block]')) return;
        this.startInteraction('click', labelOf(target), {
          tag: target.tagName.toLowerCase(),
          ...(target.id ? { id: target.id } : {}),
        });
      },
      true,
    );
    document.addEventListener(
      'keydown',
      (ev) => {
        if (isTextTarget(ev.target) || (ev.key.length === 1 && ev.key !== ' ')) return;
        if (['Shift', 'Control', 'Alt', 'Meta', 'Tab', 'CapsLock'].includes(ev.key)) return;
        this.startInteraction('key', ev.key === ' ' ? 'Space' : ev.key);
      },
      true,
    );
    window.addEventListener('error', (ev) =>
      this.push({
        kind: 'error',
        name: str(ev.message || 'error', 300),
        status: 'fail',
        data: {
          source: ev.filename,
          line: ev.lineno,
          col: ev.colno,
          stack: str(ev.error?.stack ?? '', 4_000),
        },
      }),
    );
    window.addEventListener('unhandledrejection', (ev) =>
      this.push({
        kind: 'error',
        name: `unhandled rejection: ${errorText(ev.reason)}`.slice(0, 300),
        status: 'fail',
        data: { stack: str((ev.reason as Error | undefined)?.stack ?? '', 4_000) },
      }),
    );
    for (const level of ['warn', 'error'] as const) {
      const orig = console[level].bind(console);
      console[level] = (...args: unknown[]) => {
        try {
          this.push({
            kind: 'console',
            name: level,
            status: level === 'error' ? 'fail' : 'info',
            data: { args: args.map((a) => str(a, 1_000)) },
          });
        } catch {
          // never break logging
        }
        orig(...args);
      };
    }
    const nav = (how: string) => {
      this.push({
        kind: 'nav',
        name: `${how} ${location.pathname}`,
        data: { search: location.search },
      });
      this.syncRecorder();
    };
    for (const fn of ['pushState', 'replaceState'] as const) {
      const orig = history[fn].bind(history);
      history[fn] = (...args: Parameters<History['pushState']>) => {
        orig(...args);
        nav(fn);
      };
    }
    window.addEventListener('popstate', () => nav('popstate'));
    document.addEventListener('visibilitychange', () =>
      this.push({ kind: 'lifecycle', name: `visibility ${document.visibilityState}` }),
    );
    window.addEventListener('online', () => {
      this.push({ kind: 'lifecycle', name: 'online' });
      void this.flush();
    });
    window.addEventListener('offline', () => this.push({ kind: 'lifecycle', name: 'offline' }));
    window.addEventListener('pagehide', this.onPageHide);
  }
}

/** Start the recorder once per page (later calls return the same client). */
export function initTelemetryClient(opts: TelemetryClientOptions): TelemetryClient {
  const g = globalThis as G;
  if (!g[GLOBAL]) g[GLOBAL] = new Client(opts);
  return g[GLOBAL];
}

export function getTelemetryClient(): TelemetryClient | null {
  return (globalThis as G)[GLOBAL] ?? null;
}

const NOOP_HANDLE: TrackHandle = { traceId: '', spanId: '', ok: () => {}, fail: () => {} };

/**
 * "What is supposed to happen" → `.ok(outcome)` / `.fail(error)` with what
 * happened. Safe to call when the recorder isn't running (no-op).
 */
export function track(
  name: string,
  intent?: unknown,
  opts?: { background?: boolean },
): TrackHandle {
  return getTelemetryClient()?.track(name, intent, opts) ?? NOOP_HANDLE;
}

/** "Fehler merken" (see TelemetryClient.markBug); no-op without a recorder. */
export function markBug(
  bugId: string,
  context?: Record<string, unknown>,
): { traceId: string | null } {
  return getTelemetryClient()?.markBug(bugId, context) ?? { traceId: null };
}

/** `traceparent` header value for a tracked operation (empty handle → undefined). */
export function traceparentOf(handle: TrackHandle): string | undefined {
  return handle.traceId && handle.spanId ? traceparent(handle.traceId, handle.spanId) : undefined;
}

export { parseTraceparent } from './ids.js';
